import { slicedToArray as _slicedToArray } from './_virtual/_rollupPluginBabelHelpers.mjs';
import { useIsMounted } from '@toss/react';
import { useRef, useContext, useEffect, useMemo, createContext } from 'react';
import { jsx } from 'react/jsx-runtime';
import { useKey } from './hooks/useKey.mjs';

var ErrorBoundaryGroupContext = /*#__PURE__*/createContext(undefined);

if (process.env.NODE_ENV !== 'production') {
  ErrorBoundaryGroupContext.displayName = 'ErrorBoundaryGroupContext';
}
/**
 * @description ErrorBoundaryGroup is Component to manage multiple ErrorBoundaries
 * @example
 * ```jsx
 * <ErrorBoundaryGroup>
 *   <ErrorBoundaryGroupReset trigger={( group ) => <button onClick={group.reset} />} />
 *   <ErrorBoundary />
 *   <ErrorBoundary />
 * </ErrorBoundaryGroup>
 *
 * const ErrorBoundaryGroupReset = ({ trigger: Trigger }) => {
 *   const group = useErrorBoundaryGroup();
 *
 *   return <Trigger {...group} />;
 * };
 * ```
 */


var ErrorBoundaryGroup = function ErrorBoundaryGroup(_ref) {
  var _ref$blockOutside = _ref.blockOutside,
      blockOutside = _ref$blockOutside === void 0 ? false : _ref$blockOutside,
      children = _ref.children;
  var blockOutsideRef = useRef(blockOutside);
  var isMounted = useIsMounted();
  var group = useContext(ErrorBoundaryGroupContext);

  var _useKey = useKey(),
      _useKey2 = _slicedToArray(_useKey, 2),
      resetKey = _useKey2[0],
      reset = _useKey2[1];

  useEffect(function () {
    if (isMounted && !blockOutsideRef.current) {
      reset();
    }
  }, [group === null || group === void 0 ? void 0 : group.resetKey, isMounted, reset]);
  var value = useMemo(function () {
    return {
      reset: reset,
      resetKey: resetKey
    };
  }, [reset, resetKey]);
  return /*#__PURE__*/jsx(ErrorBoundaryGroupContext.Provider, {
    value: value,
    children: children
  });
};
/**
 * useErrorBoundaryGroup need ErrorBoundaryGroup in parent. if no ErrorBoundaryGroup, this hook will throw Error to prevent that case.
 */

var useErrorBoundaryGroup = function useErrorBoundaryGroup() {
  var group = useContext(ErrorBoundaryGroupContext);

  if (group === undefined) {
    throw new Error('useErrorBoundaryGroup must be used within an ErrorBoundaryGroup');
  }

  return useMemo(function () {
    return {
      /**
       * When you want to reset multiple ErrorBoundaries as children of ErrorBoundaryGroup, You can use this reset
       */
      reset: group.reset
    };
  }, [group.reset]);
};

export { ErrorBoundaryGroup, ErrorBoundaryGroupContext, useErrorBoundaryGroup };
